import { Barn, Trainer, Person } from "../../models";
import { formatShortAddress } from "../address/FormatAddress";
import { getPersonByPersonId } from "../person/Person";
import { formatDisplayNameForUser } from "../person/PersonNameFormat";
import { getTrainersByBarnId, getTrainersByPersonId } from "./Trainer";
export interface formattedOption {
    value: string; //Trainer's id
    label: string; //Used for filtering options as a user types
    trainerName: string; //Trainer's Full Name - will display when item is selected
    trainerNameLabel?: string; //Trainer's Full Name
    barnLabel?: string; //Barn Name - will display in options
    locationLabel?: string //Trainer's Location - will display in options
    createdByLabel?: string;
    trainer?: Trainer;
}

export interface formattedGroupOfOptions {
    label: string;
    options: formattedOption[];
}

export async function getTrainersCreatedByUserOrInCurrentBarn(user: Person, currentBarn?: (Barn | null | undefined), listOfTrainersCreatedByUser?: (formattedOption[] | undefined)) {
    // First, get trainers by User (check if trainers created by the user were already passed in)
    let trainersCreatedByUser: (formattedOption[] | undefined) = listOfTrainersCreatedByUser || [];
    if (trainersCreatedByUser.length === 0 && user) {
        trainersCreatedByUser = await getTrainersCreatedByUser(user);
    }

    // Next, get trainers by Barn
    let trainersByBarn: (formattedOption[] | undefined) = [];
    if (currentBarn != null && currentBarn != undefined) {
        trainersByBarn = await getTrainersInCurrentBarn(currentBarn);
    }

    // Next, remove any duplicates by taking barn trainers out of the list of created by trainers
    const trainersCreatedByUserUpdatedList = await removeBarnTrainersFromCreatedByGroup(trainersCreatedByUser, trainersByBarn);

    const sortedCreatedByList = trainersCreatedByUserUpdatedList.sort((a, b) => a.trainerName.localeCompare(b.trainerName));
    const sortedBarnList = trainersByBarn?.sort((a, b) => a.trainerName.localeCompare(b.trainerName));

    const result = {
        "createdBy": sortedCreatedByList,
        "barn": sortedBarnList
    };

    return result;
};

export async function getTrainersCreatedByUser(user: Person) {
    try {
        let formattedList: formattedOption[] = [];
        const queryTrainersByCreatedByUser = await getTrainersByPersonId(user.id);
        if (queryTrainersByCreatedByUser.isSuccess) {
            const result: Trainer[] = queryTrainersByCreatedByUser.result;
            formattedList = await formatTrainers(result, user);
        }
        return formattedList;
    } catch (error) {
        console.error("Error fetching trainers created by user:", error);
        return [];
    }
};

export async function getTrainersInCurrentBarn(currentBarn: Barn) {
    try {
        let formattedList: formattedOption[] = [];
        if (currentBarn) {
            const queryTrainersByBarn = await getTrainersByBarnId(currentBarn.id);
            if (queryTrainersByBarn.isSuccess) {
                const result: Trainer[] = queryTrainersByBarn.result;
                formattedList = await formatTrainers(result);
            }
        }
        return formattedList;
    } catch (error) {
        console.error("Error fetching barn trainers:", error);
    }
};

export async function removeBarnTrainersFromCreatedByGroup(createdByList?: (formattedOption[] | null), barnTrainersList?: (formattedOption[] | null)) {
    let result: formattedOption[] = createdByList || [];
    // Only need to handle this if both of the lists have items in them
    if (createdByList && createdByList.length > 0 && barnTrainersList && barnTrainersList.length > 0) {
        // Remove all of the barnTrainers from the CreatedBy list
        result = createdByList?.filter((createdByTrainer) => !barnTrainersList.some((barnTrainer) => barnTrainer.value === createdByTrainer.value));
    }
    return result;
};

// FORMATTERS
export async function formatTrainers(trainerArray: Trainer[], user?: Person) {
    let formattedTrainers = [];

    for (var i = 0; i < trainerArray.length; i++) {
        const trainer = trainerArray[i];
        let object: formattedOption = await formatSingleTrainer(trainer, user);
        formattedTrainers.push(object);
    }

    return formattedTrainers;
};

export async function formatSingleTrainer(trainer: Trainer, user?: Person) {
    const value = trainer.id;
    const nameLabel = trainer.name || "Unknown - No name found";
    const barnLabel = trainer.barn ? trainer.barn.name : "Unknown - No barn found";
    const locationLabel = (trainer.address ? formatShortAddress(trainer.address) : (trainer.location || "Unknown - No location found"));
    let createdByLabel = "Unknown";
    
    // If the trainer is in the CreatedBy group, then we know the user who created it.
    // Saves us from querying the DB any more times than needed
    if (user) {
        createdByLabel = formatDisplayNameForUser(user);
    }
    
    // If we only have the personId, then we will make the DB query to get the createdBy person's name
    if (createdByLabel == "Unknown" && trainer.createdBy) {
        const personResult = await getPersonByPersonId(trainer.createdBy);
        if (personResult.isSuccess) {
            const person: Person = personResult.result;
            createdByLabel = formatDisplayNameForUser(person);
        }
    }

    let object: formattedOption = {
        value: value,
        label: nameLabel,
        trainerName: nameLabel,
        trainerNameLabel: nameLabel,
        barnLabel: barnLabel,
        locationLabel: locationLabel,
        createdByLabel: createdByLabel,
        trainer: trainer,
    };
    return object;
};